import React, { useState, useEffect } from 'react';
import { X, MapPin, Home, Building, BedDouble, Bath, Users } from 'lucide-react';
import { Property } from '../../types';

interface PropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (property: Property) => void;
  ownerId: string; // Le bien est toujours lié à un owner
  propertyToEdit?: Property | null;
}

export const PropertyModal: React.FC<PropertyModalProps> = ({ isOpen, onClose, onSave, ownerId, propertyToEdit }) => {
  const [formData, setFormData] = useState<Partial<Property>>({
    address: '',
    city: '',
    zipCode: '',
    type: 'Appartement',
    surface: 0,
    bedrooms: 1,
    bathrooms: 1,
    maxGuests: 2
  });

  const [suggestions, setSuggestions] = useState<any[]>([]);

  // Initialisation à l'ouverture
  useEffect(() => {
    if (isOpen) {
      if (propertyToEdit) {
        setFormData({ ...propertyToEdit });
      } else {
        setFormData({
            ownerId, // Link to owner
            address: '',
            city: '',
            zipCode: '',
            type: 'Appartement',
            surface: 0,
            bedrooms: 1,
            bathrooms: 1,
            maxGuests: 2
        });
      }
      setSuggestions([]);
    }
  }, [isOpen, propertyToEdit, ownerId]);

  if (!isOpen) return null;

  // Gestion API Adresse Gouv
  const handleAddressSearch = async (query: string) => {
    setFormData({ ...formData, address: query });
    if (query.length > 3) {
      try {
        const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
        const data = await res.json();
        setSuggestions(data.features || []);
      } catch (e) {
        console.error("Address API error", e);
      }
    } else {
      setSuggestions([]);
    }
  };

  const selectAddress = (feature: any) => {
    setFormData({
      ...formData,
      address: feature.properties.name,
      city: feature.properties.city,
      zipCode: feature.properties.postcode,
    });
    setSuggestions([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.address && formData.city) {
        onSave({
            ...formData,
            ownerId // Ensure ownerId is preserved
        } as Property);
        onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
          <h3 className="text-lg font-bold text-slate-900 flex items-center">
            <Home className="w-5 h-5 mr-2 text-amber-500" />
            {propertyToEdit ? 'Modifier le bien' : 'Ajouter un bien'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          
          {/* ADRESSE */}
          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse (Recherche auto)</label>
            <div className="relative">
                <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                    type="text"
                    required
                    className="w-full pl-9 p-2 border border-slate-300 rounded focus:ring-amber-500 focus:border-amber-500 bg-white text-slate-900"
                    placeholder="Commencez à saisir l'adresse..."
                    value={formData.address}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                />
            </div>
            {suggestions.length > 0 && (
                <ul className="absolute z-10 w-full bg-white border border-slate-300 rounded-md mt-1 shadow-lg max-h-48 overflow-auto">
                    {suggestions.map((s, i) => (
                        <li key={i} onClick={() => selectAddress(s)} className="p-2 hover:bg-amber-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900">
                            <span className="font-bold">{s.properties.name}</span> <span className="text-slate-500">{s.properties.postcode} {s.properties.city}</span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2">
                <input type="text" placeholder="Code Postal" readOnly className="p-2 bg-slate-50 border rounded text-xs text-slate-500" value={formData.zipCode} />
                <input type="text" placeholder="Ville" readOnly className="p-2 bg-slate-50 border rounded text-xs text-slate-500" value={formData.city} />
            </div>
          </div>

          {/* DETAILS TECHNIQUES */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de bien</label>
              <div className="relative">
                  <Building className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <select 
                    className="w-full pl-9 p-2 border border-slate-300 rounded bg-white focus:ring-amber-500 focus:border-amber-500 text-slate-900"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value as any})}
                  >
                    <option>Appartement</option>
                    <option>Maison</option>
                    <option>Immeuble</option>
                    <option>Autre</option>
                  </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Surface (m²)</label>
              <input
                type="number"
                min="0"
                className="w-full p-2 border border-slate-300 rounded focus:ring-amber-500 focus:border-amber-500 bg-white text-slate-900"
                value={formData.surface}
                onChange={(e) => setFormData({...formData, surface: Number(e.target.value)})}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
             <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 flex items-center"><BedDouble className="w-3 h-3 mr-1"/> Chambres</label>
                  <input type="number" min="0" className="w-full p-2 border rounded text-sm bg-white text-slate-900" 
                     value={formData.bedrooms || 0} 
                     onChange={e => setFormData({...formData, bedrooms: Number(e.target.value)})}
                  />
             </div>
             <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 flex items-center"><Bath className="w-3 h-3 mr-1"/> SDB</label>
                  <input type="number" min="0" className="w-full p-2 border rounded text-sm bg-white text-slate-900" 
                     value={formData.bathrooms} 
                     onChange={e => setFormData({...formData, bathrooms: Number(e.target.value)})}
                  />
             </div>
             <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1 flex items-center"><Users className="w-3 h-3 mr-1"/> Voyageurs</label>
                  <input type="number" min="0" className="w-full p-2 border rounded text-sm bg-white text-slate-900" 
                     value={formData.maxGuests} 
                     onChange={e => setFormData({...formData, maxGuests: Number(e.target.value)})}
                  />
             </div>
          </div>

          <div className="pt-4 flex justify-end space-x-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors font-medium shadow-sm"
            >
              {propertyToEdit ? 'Mettre à jour' : 'Créer le bien'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};